/**
 * The structured Daily Log — site hero strip, then category cards
 * (spec §3.1, §5.2).
 *
 * Renders `GET /api/daily-log`, i.e. `getDailyLog`. The layout follows the
 * design's DAILY LOG block: the black hero card with one tile per site, the
 * three-column grid of category cards, and the quiet-categories line beneath
 * them.
 *
 * ## `cards`, `quietCategories` and `outsideTaxonomy` are all rendered
 *
 * The first two PARTITION the canonical 18-category taxonomy, and that
 * partition is the whole point of the query:
 *
 *  - A category with something in it gets a **card**.
 *  - A category with nothing gets **named in the quiet line** — never dropped
 *    (a view whose sections silently appear and disappear with the data
 *    cannot be scanned for absence, and "nothing was logged under Safety
 *    today" is a fact a superintendent acts on) and never rendered as an
 *    empty card (18 cards of which 13 are empty is a page you stop reading).
 *  - A daily-log row whose category is OUTSIDE the taxonomy — a value
 *    predating the enum, which is not revalidated on read — gets neither, so
 *    it joins the timeline under the walk it came from rather than being
 *    dropped. A lost record is the failure this console exists to prevent.
 *
 * Collapsing any of the three into the others would lose exactly the
 * information the split was built to keep.
 *
 * ## Observations are NOT here, and that is not the same as losing them
 *
 * Procore files observations under the daily log; this console does not.
 * `getDailyLog` drops every observation-scoped category before it counts
 * anything, because an observation has a first-class destination of its own —
 * the Observations list, its own left-nav row, its own count. Carrying it
 * here as well showed one record on two screens and padded this one's
 * numbers: the `All N` chip, the day picker's per-day counts and the
 * `Needs OK` chip all counted rows this report was never going to be about. The invariant was always the more precise one — no row
 * is lost — and a row that is shown somewhere better is not lost.
 *
 * ## The day
 *
 * `getDailyLog` takes an INSTANT range, not a calendar date, because "which
 * day is this log on" has a timezone in it and the query is the wrong place
 * to answer that. This view is the right place: it converts the browser's own
 * local day into a half-open `[from, to)` and passes it. `?day=YYYY-MM-DD`
 * overrides, so a shared link resolves the same day for everyone who opens
 * it.
 *
 * ## The chips are controls now, and the one that cannot be says so
 *
 * `When` and `Site` used to render as chips — border, chevron, the whole
 * affordance — and do nothing: the source design's handlers were empty and
 * spec §6.3 shipped them inert for v1. Together with a hero that printed one
 * tile per site for every site in the workspace, that produced a page a
 * reader could fairly describe as purely decorative — nothing to click, and
 * the two sites that had actually logged something buried under sixty-eight
 * tiles reading "NO ENTRY".
 *
 * `When` is a day stepper (and its label returns to today). `Site` is a menu
 * over the same sites the hero names, each with its entry count for the day,
 * writing `?site=` — which the route already intersects with the caller's
 * ceiling, so the control cannot widen anything. The hero's tiles write the
 * same parameter, because a site tile is the obvious place to ask "just this
 * one" from and nothing offered it.
 *
 * `Group` is NOT a control: this view groups by category and has no second
 * grouping to offer. It renders as plain state text rather than as a chip,
 * because a control that appears live and quietly does nothing spends the
 * reader's trust once and takes the rest of the page down with it.
 */

import { useEffect, useRef, useState } from "react";
import {
  fetchDailyLog,
  fetchWalkPhotos,
  type ConsoleWalkPhoto,
  type ApiError,
  type ConsoleRow,
  type ConsoleSite,
  type DailyLogDayRow,
  type DailyLogResponse,
  type DailyLogAutodeskTemplate,
  type DailyLogWalkRow,
} from "../api";
import { useCoalescedResource } from "../useResource";
import { ADR, C, DL, FONT_MONO } from "../tokens";
import {
  Card,
  ConsoleViewProps,
  EmptyPanel,
  FailurePanel,
  META,
  QuietLine,
  StaleBanner,
  ViewBody,
  ViewHeader,
  MONTHS,
  WEEKDAYS_SHORT,
  dayLabel,
  plural,
  scopedSearch,
} from "../components/chrome";
import { JumpToDate } from "../components/JumpToDate";
import { SkeletonBlock } from "../components/Skeleton";
import { DailyLogEntryEditor } from "./DailyLogEntryEditor";
import { DailyLogPhotosPane } from "./DailyLogPhotosPane";
import {
  DAILY_LOG_BADGE_COLUMN, DAILY_LOG_BADGE_TYPE, DailyLogFilterPill, FlatTable, GroupByWalkToggle, ManpowerTable,
  DailyLogStyles, WalkGroupCard, WalkHeader, type DailyLogRowHandlers,
} from "./DailyLogTables";
import { buildWalkGroups, flattenGroups } from "./dailyLogModel";
import { AutodeskDayReports, AutodeskReportStrip } from "./AutodeskDayReports";
import { AutodeskDailyReport } from "./AutodeskDailyReport";
import { reviewRecordHref } from "../lib/reviewHref";

// ---------------------------------------------------------------------------
// The day
// ---------------------------------------------------------------------------

/** Local midnight of a `YYYY-MM-DD`, or of today. Built from the parts rather
 *  than `new Date("2026-08-26")` — that parses as UTC midnight, which is the
 *  PREVIOUS day for anyone west of Greenwich, and a daily log on the wrong
 *  day is worse than no daily log. */
function localMidnight(raw: string | null, now: Date): Date {
  const match = raw?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export interface ResolvedDay {
  from: Date;
  to: Date;
  isToday: boolean;
  label: string;
  /** Local midnight of the reader's OWN today, from the same `now` `isToday`
   *  was decided against. Carried so the week strip can mark today without
   *  reading a second clock — and so it cannot disagree with `isToday`, which
   *  is exactly what happened when the strip took its today from the
   *  response's `generatedAt` instead. */
  today: Date;
}

/** The half-open `[from, to)` this view asks for, and what to call it. */
export function resolveDay(search: string, now = new Date()): ResolvedDay {
  const from = localMidnight(new URLSearchParams(search).get("day"), now);
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isToday = from.getTime() === today.getTime();
  return { from, to, isToday, today, label: isToday ? "Today" : dayLabel(from) };
}

/**
 * The parameters that are this PAGE's view state and not the query's.
 *
 * Both narrow the report in the browser, over rows the response already
 * holds: `cat` is matched against `row.category` and `needsok` against the
 * review state, and neither is read by `/api/daily-log` — `handleDailyLog`
 * takes `from`, `to`, `site` and `sort`, and nothing else.
 *
 * They still live in the URL, because a filtered day is a link worth
 * pasting. They simply must not reach the fetch key.
 */
const VIEW_ONLY_PARAMS = ["cat", "needsok", "flat"] as const;

/**
 * The console URL, plus the day as the instant range the route takes.
 *
 * Every other parameter is forwarded verbatim — `workspace` above all, and
 * `site`, which the route intersects with the caller's ceiling — EXCEPT the
 * page's own view state.
 *
 * Dropping those two is what stops a category chip reloading the page. This
 * string is the `useResource` key, so putting `cat` in it made every chip
 * press a new key: the hook blanked its data and refetched a response
 * identical to the one already on screen, and the whole report went to a
 * skeleton and came back. The filtering was client-side the entire time; the
 * only thing the round trip changed was that the reader had to watch it.
 */
export function dailyLogQuery(search: string, day: ResolvedDay): string {
  const params = new URLSearchParams(search);
  params.set("from", day.from.toISOString());
  params.set("to", day.to.toISOString());
  for (const key of VIEW_ONLY_PARAMS) params.delete(key);
  return `?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Category tone
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

interface SiteTile {
  site: ConsoleSite;
  entries: number;
  /** The two busiest categories on this site, already labelled. */
  top: string[];
}

function buildTiles(data: DailyLogResponse): SiteTile[] {
  return data.hero.sites.map((site) => {
    const counts = new Map<string, number>();
    let entries = 0;
    for (const card of data.cards) {
      const rows = card.rows.filter((row) => row.jobSiteId === site.id);
      if (rows.length === 0) continue;
      entries += rows.length;
      counts.set(card.label, rows.length);
    }
    const top = [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 2)
      // `×2` rather than the design's "2 DELIVERIES": the design carries an
      // explicit singular map for all 18 labels ("Deliveries" -> "Delivery")
      // and we do not, so "1 Deliveries" would be the common case.
      .map(([label, count]) => `${label} ×${count}`);
    return { site, entries, top };
  });
}

/**
 * The quiet line, in the design's own words.
 *
 * 8a writes it as a LIST, not as a sentence about a list:
 * *"Quiet today: Delays, Visitors, Phone calls, Inspections, Equipment,
 * Accidents."* — the labels as they are printed everywhere else, capitalised,
 * comma-separated, ending in a full stop. It used to read "Nothing logged
 * under delays, visitors — and 11 other categories were quiet today", which
 * spends its first four words on grammar and lowercases the one part a reader
 * is scanning for.
 *
 * The cap stays. The design's artboard has six quiet categories; a real day
 * often has thirteen, and naming all of them turns a footnote into a
 * paragraph. Six are named and the rest are counted, so the line is bounded
 * without ever claiming a category was logged when it was not.
 */
export function quietSentence(labels: string[], day: ResolvedDay): string {
  const when = day.isToday ? "today" : "that day";
  if (labels.length === 0) return `Every category logged something ${when}.`;
  const named = labels.slice(0, 6);
  const rest = labels.length - named.length;
  return (
    `Quiet ${when}: ${named.join(", ")}` +
    (rest > 0 ? `, and ${rest} more.` : ".")
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

/**
 * These used to be inert.
 *
 * All three rendered as chips — border, chevron, the whole affordance — and
 * did nothing, because the source design's handlers were empty and spec §6.3
 * shipped them that way for v1. That is the failure `backendGateway.ts` names
 * about mutation controls, in read-only form: a control that appears live and
 * quietly does nothing is worse than no control, because the reader spends
 * their trust on it once and stops believing the rest of the page.
 *
 * `When` and `Site` are now real. `Group` still is not — the daily log groups
 * by category and there is no second grouping for it to offer — so it no
 * longer pretends: it renders as plain state text (`StateChip`), which says
 * the same true thing without promising a menu behind it.
 */
function chipShell(active?: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 7,
    // 36/10/hairline, matching the stepper's arrows either side of it. It
    // was 32/9 on the heavier `line2`, which read as a filter chip sitting
    // between two buttons rather than as the middle of one control.
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    background: active ? C.surface : C.surface,
    border: `1px solid ${C.hair}`,
  };
}

function chipLabel(text: string) {
  return <span style={{ ...META, fontSize: 10.35, letterSpacing: "0.7px", color: C.sub2 }}>{text}</span>;
}

function chipValue(text: string) {
  return <span style={{ fontSize: 14.37, fontWeight: 600, color: C.ink }}>{text}</span>;
}

function Caret() {
  return (
    <svg width="8" height="5" viewBox="0 0 10 6" fill="none" aria-hidden>
      <path
        d="M1 1l4 4 4-4"
        stroke={C.sub2}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StepChevron({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg width="7" height="11" viewBox="0 0 8 12" fill="none" aria-hidden>
      <path
        d={direction === "prev" ? "M6.5 1L1.5 6l5 5" : "M1.5 1l5 5-5 5"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * `YYYY-MM-DD` in the READER's zone — the same spelling `?day=` is parsed in.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that is the UTC day, and west
 * of UTC an evening would step to tomorrow's date while the page still said
 * today.
 */
export function dayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const dayOfMonth = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${dayOfMonth}`;
}

/** `day.from` shifted by whole days, in local time. */
function shiftDay(from: Date, days: number): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/** Monday-start week containing `from` — the seven days the strip draws. */
function weekOf(from: Date): Date[] {
  // getDay() is Sunday-0; the design's week starts Monday, so Sunday is 6
  // days INTO the week rather than the first of the next one.
  const offset = (from.getDay() + 6) % 7;
  const monday = shiftDay(from, -offset);
  return Array.from({ length: 7 }, (_, i) => shiftDay(monday, i));
}

/** `Aug 31` — the month is worn only where the strip crosses one. */
function monthDayLabel(date: Date): string {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/**
 * The week strip — the daily log's date navigation (design 11a).
 *
 * ## Why this replaced the ‹ Today › stepper
 *
 * The stepper moved one calendar day at a time and said NOTHING about the day
 * it was about to land on, so a reader hunting for the day of the slab pour
 * pressed the arrow until something showed. The list of days that had logged
 * something lived behind the middle chip, which meant the one fact that made
 * the arrows usable was the one fact you had to open a menu to see.
 *
 * The strip inverts that: seven days on screen, each wearing its own count and
 * its own review state, and the arrows move a WEEK. You can see where the
 * entries are before you click — which is the whole job the menu was doing,
 * done without a click.
 *
 * ## The two golds
 *
 * `weekPending` (10%) marks a day with entries still waiting on someone;
 * `weekSelected` (16%) marks the day on screen. A day that is both takes the
 * stronger tint, so "where I am" is never hidden under "what needs doing".
 *
 * ## An empty day is still a day
 *
 * A cell with nothing logged dims its number and drops the count line for a
 * single grey dot. It stays CLICKABLE: "was anything logged on Saturday" is a
 * real question, and a strip that refuses to answer it sends the reader back
 * to editing the URL. What it must not do is look like a day with entries.
 *
 * ## Forward stops at the week containing today
 *
 * There is no daily log for next week. The design dims that arrow; disabling
 * it says the same thing to the keyboard.
 */
function WeekStrip({
  day,
  days,
  onPick,
  style,
  countWalks = false,
}: {
  day: ResolvedDay;
  /** The Autodesk report counts walks, the report's unit, not entries. */
  countWalks?: boolean;
  /** Every day in scope that logged something — the strip reads counts off
   *  it rather than asking for a second query. */
  days: DailyLogDayRow[];
  /** `null` clears `?day=` — see the today cell. */
  onPick: (key: string | null) => void;
  style?: React.CSSProperties;
}) {
  const week = weekOf(day.from);
  const byDay = new Map(days.map((entry) => [entry.day, entry]));
  const activeKey = dayKey(day.from);
  const todayKey = dayKey(day.today);
  // The strip may span two months. The design wears the month on the FIRST
  // cell only — enough to place the week, without repeating it seven times.
  const spansMonths = week[0]!.getMonth() !== week[6]!.getMonth();
  // Forward is dead once today is on screen: there is no log for next week.
  const atCurrentWeek = week.some((date) => dayKey(date) === todayKey);

  return (
    <div
      data-testid="week-strip"
      style={{
        display: "grid",
        gridTemplateColumns: "40px repeat(7, minmax(0, 1fr)) 40px",
        background: C.surface,
        border: `1px solid ${C.hair}`,
        borderRadius: 14,
        boxShadow: `0 1px 2px ${C.softShadow}`,
        overflow: "hidden",
        ...style,
      }}
    >
      <WeekArrow
        direction="prev"
        label="Previous week"
        testId="week-prev"
        onClick={() => onPick(dayKey(shiftDay(day.from, -7)))}
      />
      {week.map((date, index) => {
        const key = dayKey(date);
        const entry = byDay.get(key);
        const isActive = key === activeKey;
        const isToday = key === todayKey;
        const pending = (entry?.pendingReview ?? 0) > 0;
        // There is no daily log for tomorrow. The week runs Monday to Sunday,
        // so on any day but Sunday the strip DRAWS days that have not
        // happened — they stay on screen, because the week is the week, but
        // they are not destinations. This is the same rule the forward arrow
        // enforces at the end of the strip, applied inside it.
        const isFuture = key > todayKey;
        return (
          <button
            type="button"
            className="kb"
            key={key}
            data-testid="daily-log-day"
            data-day={key}
            data-active={isActive ? "true" : "false"}
            data-future={isFuture ? "true" : undefined}
            aria-current={isActive ? "date" : undefined}
            disabled={isFuture}
            title={
              isFuture
                ? `${dayMenuLabel(key)} — hasn't happened yet`
                : entry
                  ? `${dayMenuLabel(key)} — ${plural(entry.entries, "entry", "entries")}`
                  : `${dayMenuLabel(key)} — nothing logged`
            }
            // Today clears the parameter rather than writing today's date
            // into it: a link that pinned today would still say today
            // tomorrow, and this page gets sent to people.
            onClick={() => onPick(isToday ? null : key)}
            aria-disabled={isFuture || undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 5,
              padding: "12px 0 11px",
              borderRight: index === 6 ? "none" : `1px solid ${C.hairRow}`,
              background: isActive
                ? C.weekSelected
                : pending
                  ? C.weekPending
                  : "transparent",
              // A future day is drawn, not offered: the same 35% the forward
              // arrow wears, so the two read as one rule rather than two.
              opacity: isFuture ? 0.35 : 1,
              cursor: isFuture ? "default" : "pointer",
            }}
          >
            <span
              style={{
                ...META,
                fontSize: 10.35,
                letterSpacing: "0.9px",
                whiteSpace: "nowrap",
                fontWeight: isActive ? 700 : 600,
                color: isActive ? C.weekOn : C.weekLabel,
              }}
            >
              {isToday ? "Today" : WEEKDAYS_SHORT[date.getDay()]}
            </span>
            {isActive ? (
              <span
                style={{
                  // The design's disc: the day on show, ink on its gold ground.
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  background: C.ink,
                  color: C.surface,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 17.25,
                  fontWeight: 700,
                }}
              >
                {date.getDate()}
              </span>
            ) : (
              <span
                style={{
                  fontSize: 17.25,
                  fontWeight: 600,
                  color: entry ? C.ink : C.weekDim,
                }}
              >
                {spansMonths && index === 0 ? monthDayLabel(date) : date.getDate()}
              </span>
            )}
            {entry ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  // A day with entries still waiting reads amber (the design's
                  // "2 · 2 waiting"), not only its dot.
                  color: pending ? DL.conflictInk : isActive ? C.weekCountOn : C.weekCount,
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 3,
                    background: pending ? C.amber : C.green,
                  }}
                />
                {countWalks
                  ? pending
                    ? `${plural(entry.walks ?? 1, "walk", "walks")} · ${entry.pendingReview} waiting`
                    : plural(entry.walks ?? 1, "walk", "walks")
                  : pending
                    ? `${entry.entries} · ${entry.pendingReview} waiting`
                    : plural(entry.entries, "entry", "entries")}
              </span>
            ) : (
              <span
                aria-hidden
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 3,
                  background: C.weekDot,
                  marginTop: 6,
                }}
              />
            )}
          </button>
        );
      })}
      <WeekArrow
        direction="next"
        label="Next week"
        testId="week-next"
        disabled={atCurrentWeek}
        onClick={() => onPick(dayKey(shiftDay(day.from, 7)))}
      />
    </div>
  );
}

/** One end of the strip. A full-height cell, not a floating button: the
 *  design fuses the arrows into the same grid so the strip reads as one
 *  control rather than as three. */
function WeekArrow({
  direction,
  label,
  testId,
  onClick,
  disabled = false,
}: {
  direction: "prev" | "next";
  label: string;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="kb"
      data-testid={testId}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "grid",
        placeItems: "center",
        background: "transparent",
        color: C.sub,
        borderRight: direction === "prev" ? `1px solid ${C.hairRow}` : "none",
        borderLeft: direction === "next" ? `1px solid ${C.hairRow}` : "none",
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <StepChevron direction={direction} />
    </button>
  );
}

/**
 * 10a — the quiet day on a LIVE project.
 *
 * The empty daily log used to be the same sad glasses icon whether the
 * workspace had never been walked or a busy job site simply had not been
 * walked YET today. Those are different moments and want different pages:
 * the second one is normal, it is 9am, and the useful thing to say is where
 * the entries actually are. The week strip above this panel answers that at a
 * glance; this panel names the nearest day with entries and hands the reader
 * a button to it.
 *
 * ## The design's second button is not here
 *
 * 10a draws `Add an entry by hand` beside the jump. The console owns no
 * writes — there is no route that creates a daily-log record, and the one
 * write path it does have (review accept) needs a record to act on. A button
 * that opened nothing would be the exact failure this file's header calls
 * out: "a control that appears live and quietly does nothing spends the
 * reader's trust once and takes the rest of the page down with it." So the
 * row carries the one button that goes somewhere real.
 */
function QuietDayPanel({
  day,
  reportLabel = "Daily log",
  siteWord,
  walksThatDay,
  nearest,
  onNavigate,
  hrefWithParam,
}: {
  day: ResolvedDay;
  reportLabel?: string;
  /** What to call the project, or `null` when the scope is several of them
   *  and there is no one name to use. Never a raw id (10a). */
  siteWord: string | null;
  /** How many walks the day actually had. Zero and non-zero are different
   *  days and get different sentences — see the copy below. */
  walksThatDay: number;
  /** The nearest day in scope that logged something, or `null` when nothing
   *  ever has. */
  nearest: DailyLogDayRow | null;
  onNavigate: (href: string) => void;
  hrefWithParam: (key: string, value: string | null) => string;
}) {
  /*
   * This panel names DAILY LOG ENTRIES, not walks.
   *
   * It used to read "No walks logged that day" over "Nothing was walked here
   * that day" — on days the superintendent had walked. What is empty is this
   * page, and the only fact the page holds is that no entry landed on it. A
   * walk is a different thing, it happens on the glasses, and it can finish
   * without logging: one that raised only observations or only tasks logs
   * nothing here by design. Telling someone their walk did not happen is
   * both wrong and the kind of wrong that makes them stop trusting the log.
   *
   * So `walksThatDay` decides the second line, and only that line:
   *   0  — nobody was on site; saying so is safe and is useful context.
   *   1+ — the day was walked and produced no entry, which is a real state
   *        and worth naming, not papering over.
   */
  const title = siteWord
    ? day.isToday
      ? `No ${reportLabel.toLowerCase()} entries on ${siteWord} yet today`
      : `No ${reportLabel.toLowerCase()} entries on ${siteWord} that day`
    : day.isToday
      ? `No ${reportLabel.toLowerCase()} entries yet today`
      : `No ${reportLabel.toLowerCase()} entries that day`;

  const opening = day.isToday
    ? walksThatDay > 0
      ? `${plural(walksThatDay, "walk", "walks")} so far today, and nothing logged here yet.`
      : "Start a walk on your glasses or phone and the log writes itself."
    : walksThatDay === 1
      ? "One walk that day, and it logged nothing here."
      : walksThatDay > 1
        ? `${walksThatDay} walks that day, and none of them logged anything here.`
        : "Nothing was walked here that day, so there was nothing to log.";
  // Only when the nearest day is BEHIND us: "last" is a claim about the past,
  // and the nearest day can be a later one when a past day is opened directly.
  const trailing =
    nearest && nearest.day < dayKey(day.from)
      ? ` ${dayMenuLabel(nearest.day)} logged ${plural(nearest.entries, "entry", "entries")}.`
      : "";

  const jumpHref = nearest ? hrefWithParam("day", nearest.day) : null;

  return (
    <Card
      testId="quiet-day-panel"
      style={{
        borderRadius: 16,
        padding: 0,
        overflow: "hidden",
        // 10a's near layer only. `Card`'s second, far layer lifts a card off
        // a page of other cards; this one IS the page, and the lift read as a
        // smudge under an otherwise empty rectangle.
        boxShadow: `0 1px 2px ${C.softShadow}`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "56px 40px 52px",
          gap: 10,
        }}
      >
        <div
          data-testid="empty-state"
          style={{ fontSize: 21.85, fontWeight: 700, letterSpacing: "-0.38px" }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 16.1,
            color: C.weekCount,
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          {opening}
          {trailing}
        </div>
        {jumpHref && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 14,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <a
              className="kb"
              data-testid="empty-state-action"
              href={jumpHref}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(jumpHref);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 40,
                padding: "0 16px",
                borderRadius: 10,
                border: `1px solid ${C.line}`,
                background: C.surface,
                fontSize: 15.52,
                fontWeight: 600,
                color: C.ink,
              }}
            >
              {`Open ${dayMenuLabel(nearest!.day)}`}
              <span aria-hidden style={{ display: "flex", color: C.sub }}>
                <StepChevron direction="next" />
              </span>
            </a>
          </div>
        )}
      </div>
    </Card>
  );
}

/** `2026-08-07` as the reader's own day. Parsed from the PARTS for the same
 *  reason `localMidnight` is — `new Date("2026-08-07")` is UTC midnight, and
 *  west of Greenwich that prints the day before. */
function dayMenuLabel(day: string): string {
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return day;
  return dayLabel(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/**
 * The filter row (design "Daily log"): `All`, then one pill per category that
 * logged something — Photos counts PHOTOS, not cards — then `Needs OK` when
 * something is still waiting, and the Group by walk switch at the right.
 * Every pill writes a URL parameter, so a filtered day is a link.
 *
 * A category with nothing in it never becomes a pill; the quiet line below
 * names it instead.
 */
function FilterChips({
  scope,
  pills,
  activeCategory,
  needsOk,
  total,
  pendingTotal,
  byWalk,
  onPickCategory,
  onToggleNeedsOk,
  onToggleByWalk,
}: {
  /**
   * The project control, leading the row — only when the day spans more than
   * one project. The design's page is one project's log, named in the
   * eyebrow, where a second project picker would only repeat the sidebar's.
   */
  scope?: React.ReactNode;
  pills: Array<{ category: string; label: string; count: number }>;
  activeCategory: string | null;
  needsOk: boolean;
  total: number;
  pendingTotal: number;
  byWalk: boolean;
  onPickCategory: (category: string | null) => void;
  onToggleNeedsOk: () => void;
  onToggleByWalk: () => void;
}) {
  return (
    <div data-testid="filter-chips" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
      {scope}
      {total > 0 && (
        <>
          <DailyLogFilterPill label="All" count={total} active={activeCategory === null} onClick={() => onPickCategory(null)} />
          {pills.map((pill) => (
            <DailyLogFilterPill
              key={pill.category}
              label={pill.label}
              count={pill.count}
              active={activeCategory === pill.category}
              onClick={() => onPickCategory(activeCategory === pill.category ? null : pill.category)}
            />
          ))}
          {pendingTotal > 0 && (
            <button
              type="button"
              className="kb"
              data-testid="needs-ok-chip"
              aria-pressed={needsOk}
              onClick={onToggleNeedsOk}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 15, border: needsOk ? `1px solid ${C.amber}` : "1px solid transparent", background: needsOk ? C.pendingTint : "transparent", fontSize: 13, fontWeight: 600, color: C.sub, whiteSpace: "nowrap" }}
            >
              Needs OK
              <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, color: C.amber }}>{pendingTotal}</span>
            </button>
          )}
          <span style={{ flex: 1, minWidth: 12 }} />
          <GroupByWalkToggle on={byWalk} onToggle={onToggleByWalk} />
        </>
      )}
    </div>
  );
}

/** Kept here for `tests/guards/dailyLogBadgeFits`, which reads the badge
 *  column off this module; the tables own the numbers. */
export { DAILY_LOG_BADGE_COLUMN, DAILY_LOG_BADGE_TYPE };

/** The site filter, as a real menu over the sites the hero already names —
 *  with each one's entry count for this day, so the choice is informed rather
 *  than a list of names to guess between. */
function SiteChip({
  tiles,
  activeSiteId,
  open,
  onOpenChange,
  onPick,
  allLabel,
}: {
  tiles: SiteTile[];
  activeSiteId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onPick: (siteId: string | null) => void;
  /** What "every project" is called; the Autodesk report says "All projects". */
  allLabel?: string;
}) {
  const active = tiles.find((tile) => tile.site.id === activeSiteId);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="kb"
        data-testid="site-chip"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        style={{ ...chipShell(Boolean(activeSiteId)), cursor: "pointer" }}
      >
        {/* PROJECT, not "Site". The console says project everywhere a reader
            can see — the crumb above this row counts "11 projects", the left
            nav says Projects, the record rows name a project. "Site" was the
            one place it used the schema's word (`jobSiteId`) instead of the
            product's, and a control that names the same thing differently
            from the sentence above it reads as a different thing. */}
        {chipLabel("Project")}
        {chipValue(
          active
            ? active.site.name || active.site.id
            : tiles.length === 1
              ? tiles[0]!.site.name || tiles[0]!.site.id
              : allLabel ?? `All ${tiles.length}`,
        )}
        <Caret />
      </button>
      {open && (
        <>
          {/* Tap-outside. Below the menu, above everything else. */}
          <div
            aria-hidden
            onClick={() => onOpenChange(false)}
            style={{ position: "fixed", inset: 0, zIndex: 39 }}
          />
          <div
            role="menu"
            data-testid="site-menu"
            className="sb"
            style={{
              position: "absolute",
              zIndex: 40,
              top: 38,
              left: 0,
              minWidth: 260,
              maxHeight: 320,
              overflowY: "auto",
              background: C.surface,
              border: `1px solid ${C.line2}`,
              borderRadius: 12,
              boxShadow: `0 10px 30px ${C.scrim}`,
              padding: 6,
            }}
          >
            <SiteMenuRow
              label={`All ${tiles.length}`}
              detail=""
              active={!activeSiteId}
              onClick={() => onPick(null)}
            />
            {tiles.map((tile) => (
              <SiteMenuRow
                key={tile.site.id}
                label={tile.site.name || tile.site.id}
                detail={
                  tile.entries > 0
                    ? plural(tile.entries, "entry", "entries")
                    : "not logged"
                }
                dim={tile.entries === 0}
                active={tile.site.id === activeSiteId}
                onClick={() => onPick(tile.site.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SiteMenuRow({
  label,
  detail,
  active,
  dim,
  onClick,
  testId = "site-menu-row",
  ...rest
}: {
  label: string;
  detail: string;
  active: boolean;
  dim?: boolean;
  onClick: () => void;
  /** The day menu reuses this row and is asserted on by its own id. */
  testId?: string;
} & Record<`data-${string}`, string | undefined>) {
  return (
    <button
      type="button"
      role="menuitem"
      className="kb navrow"
      data-testid={testId}
      {...rest}
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 9,
        textAlign: "left",
        ...(active ? { background: C.faint } : {}),
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 14.95,
          fontWeight: active ? 700 : 500,
          color: dim ? C.sub : C.ink,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      {detail && (
        <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: C.sub2, flexShrink: 0 }}>
          {detail}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

/**
 * The page with its NAVIGATION intact and the day's content still coming.
 *
 * The week strip used to vanish with everything else on every day change —
 * press `‹`, the whole view is replaced by a loading panel, and the control
 * you are using disappears from under the pointer. That is what "the arrows
 * reload the screen" describes, and it is worst exactly where the strip is
 * most useful: stepping back through weeks looking for a day with something
 * on it.
 *
 * So the frame stays and only the report goes. Everything drawn here is
 * either read from the URL — the date, which day is selected — or held from
 * the last response and genuinely day-independent (the per-date counts, the
 * project scope). The filter chips and the Accept CTA are NOT here: those are
 * facts about the day being loaded, and drawing the previous day's would be
 * the lie this is careful not to tell.
 */
/**
 * How many rows to draw for a day known to hold `entries` of them.
 *
 * Not `entries` itself: a day with forty records would draw forty bars and
 * then settle into a grouped timeline that is nothing like forty bars. Three
 * to six is the range the real report actually occupies before it scrolls,
 * and a placeholder only has to reserve the space above the fold.
 */
function skeletonRows(entries: number | null): number {
  if (entries === null) return 4;
  return Math.max(1, Math.min(entries, 6));
}

function DailyLogFrame({
  day,
  reportLabel,
  days,
  entries,
  scopeLabel,
  onPickDay,
}: {
  day: ResolvedDay;
  reportLabel: string;
  days: DailyLogDayRow[];
  /**
   * How many entries the day being loaded is known to have, or `null` when
   * that is not known (the first load, before anything is held).
   *
   * This is the one thing that makes the placeholder MATCH: the per-date
   * counts are already held for the strip, so the shape of the page being
   * loaded is usually known before it arrives. An empty day gets the empty
   * panel's outline rather than a stack of rows that collapses into it.
   */
  entries: number | null;
  scopeLabel?: string;
  onPickDay: (key: string | null) => void;
}) {
  return (
    <>
      <ViewHeader
        wide="record"
        crumb={scopeLabel ? `${reportLabel} · ${scopeLabel}` : reportLabel}
        title={dayLabel(day.from)}
        note="Loading…"
      >
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <WeekStrip
            day={day}
            days={days}
            onPick={onPickDay}
            style={{ flex: 1, minWidth: 0 }}
          />
          <JumpToDate
            day={dayKey(day.from)}
            today={day.today}
            days={days}
            onPick={onPickDay}
          />
        </div>
        {/* The filter row's height, with nothing in it: the strip above must
            not jump down when the chips arrive. 36px is the row the real
            chips occupy, measured rather than guessed. */}
        <div style={{ marginTop: 14, paddingBottom: 4, height: 40 }} />
      </ViewHeader>

      <ViewBody wide="record">
        <div data-testid="loading-state" data-skeleton="daily-log" role="status" aria-label={`Loading ${reportLabel.toLowerCase()}`} aria-busy="true">
          {entries === 0 ? (
            /* The day is KNOWN to be empty, so the placeholder is the empty
               panel's shape — one centred card — and not a stack of rows
               that collapses into it a moment later. */
            <Card
              style={{ borderRadius: 16, padding: 0, boxShadow: `0 1px 2px ${C.softShadow}` }}
            >
              {/* `QuietDayPanel`'s own box: the same padding, the same gap,
                  and the same three things in it — heading, one line of
                  explanation, and the jump button. Anything less and the card
                  grows the moment the real one arrives. */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "56px 40px 52px",
                  gap: 10,
                }}
              >
                <SkeletonBlock height={24} width={330} radius={6} />
                <SkeletonBlock height={21} width={410} radius={5} />
                <SkeletonBlock height={40} width={196} radius={10} style={{ marginTop: 14 }} />
              </div>
            </Card>
          ) : (
            /* A day with entries: the timeline's own shape — a walk header
               and its rows, as many as the day is known to hold. */
            <Card style={{ borderRadius: 16, padding: 0, boxShadow: `0 1px 2px ${C.softShadow}` }}>
              <div style={{ padding: "15px 18px", borderBottom: `1px solid ${C.hairRow}` }}>
                <SkeletonBlock height={14} width={196} radius={5} />
              </div>
              {Array.from({ length: skeletonRows(entries) }, (_, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    // 54px a row, which with the 44px header above makes the
                    // 98px a walk group actually occupies for one entry.
                    padding: "18px",
                    borderTop: index === 0 ? "none" : `1px solid ${C.hairRow}`,
                  }}
                >
                  <SkeletonBlock height={11} width={38} radius={4} />
                  <SkeletonBlock height={18} width={104} radius={5} />
                  <SkeletonBlock height={14} width={index % 2 ? 236 : 310} radius={5} />
                </div>
              ))}
            </Card>
          )}
        </div>
      </ViewBody>
    </>
  );
}

/** The URL without the page's own view state — what a read is keyed on, so a
 *  filter pill or the Group by walk switch never refetches anything. */
function readSearch(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [...VIEW_ONLY_PARAMS, "day"]) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

/** Every listed walk's photos, read once per walk and kept by walk id. A walk
 *  whose read fails is simply absent — its Photos row falls back to the
 *  Photos card's own previews (`buildWalkGroups`). */
function useDayWalkPhotos(visitIds: string[], search: string, revision: number): Map<string, ConsoleWalkPhoto[]> {
  const [photos, setPhotos] = useState<Map<string, ConsoleWalkPhoto[]>>(() => new Map());
  const key = visitIds.join(",");
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    void Promise.all(key.split(",").map((visitId) =>
      fetchWalkPhotos(visitId, search)
        .then((response) => [visitId, response.photos] as const)
        .catch(() => null),
    )).then((results) => {
      if (cancelled) return;
      setPhotos((previous) => {
        const next = new Map(previous);
        for (const result of results) if (result) next.set(result[0], result[1]);
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [key, search, revision]);
  return photos;
}

export function DailyLog(props: ConsoleViewProps & { integration?: ConsoleSite["syncSource"] }) {
  const reportLabel = props.integration === "autodesk" ? "Daily report" : "Daily log";
  const {
    search,
    refreshKey,
    personalHref,
    onGoPersonal,
    hrefForItem,
    onOpenItem,
    onNavigate: navigate,
    hrefWithParam,
  } = props;

  /** Which site menu is open, and which entry the panel is editing. Neither
   *  is "what page you are on", so neither belongs in the URL — the filters
   *  above them do, and are. */
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** The walk whose Photos row is open in the pane, and the photo focused in it. */
  const [selectedPhotosWalk, setSelectedPhotosWalk] = useState<string | null>(null);
  const [focusPhoto, setFocusPhoto] = useState<string | null>(null);
  const [editRevision, setEditRevision] = useState(0);
  const [photosRevision, setPhotosRevision] = useState(0);
  const beforeLeave = useRef<(() => Promise<boolean>) | null>(null);
  const selectEntry = async (id: string | null) => {
    if (!beforeLeave.current || await beforeLeave.current()) {
      setSelectedId(id);
      setSelectedPhotosWalk(null);
      setFocusPhoto(null);
    }
  };
  const openPhotos = async (visitId: string) => {
    if (!beforeLeave.current || await beforeLeave.current()) {
      setSelectedId(null);
      setSelectedPhotosWalk(visitId);
      setFocusPhoto(null);
    }
  };

  const onNavigate = (href: string) => {
    void (async () => {
      if (!beforeLeave.current || await beforeLeave.current()) navigate(href);
    })();
  };

  const day = resolveDay(search);
  const query = dailyLogQuery(scopedSearch(search, props.scopeSiteId), day);

  const { data, failure, refreshFailure } = useCoalescedResource<DailyLogResponse>(
    `daily-log${query}`,
    () => fetchDailyLog(query),
    refreshKey + editRevision,
  );
  // Each walk's photos, for its ONE Photos row — the Walk photos read, so the
  // log and the review step agree on which photo is whose.
  const walkPhotos = useDayWalkPhotos(
    (data?.walks ?? []).filter((walk) => !walk.autodeskTemplate).map((walk) => walk.visitId),
    readSearch(search),
    refreshKey + editRevision + photosRevision,
  );
  const flat = new URLSearchParams(search).get("flat") === "1";

  /**
   * What the page keeps while the next day loads.
   *
   * `useResource` blanks its data on a key change, and it is right to: a view
   * that kept the old workspace's rows under a new workspace's header would
   * be lying. But the week strip is not the day's content — it is the
   * NAVIGATION, and pressing its arrow to have it disappear and come back is
   * the "reloads the screen" this fixes (user, 2026-09-11: "pressing the left
   * and right arrows should not reload the screen").
   *
   * Only two things are held, and both are day-independent: the per-date
   * counts (see the note at the call site) and the scope label, which names
   * the project filter rather than the date. Everything the day actually owns
   * — the entries, the category chips, the pending count — is not held and
   * not drawn until it arrives.
   */
  const held = useRef<{ days: DailyLogDayRow[]; scopeLabel: string } | null>(null);
  /**
   * Each Autodesk walk's report as last drawn. A publish rewrites the walk's
   * sync state several times, and a read can land on a write without the
   * layout plan; the server says so (`autodeskPublishing`) rather than
   * dropping the report, and the page keeps this one meanwhile — otherwise
   * the editor would unmount to "No daily report" and lose what is typed.
   */
  const reportTemplates = useRef(new Map<string, DailyLogAutodeskTemplate>());

  if (failure) {
    return (
      <FailurePanel
        failure={failure as ApiError}
        personalHref={personalHref}
        onGoPersonal={onGoPersonal}
      />
    );
  }
  if (!data) {
    return (
      <DailyLogFrame
        day={day}
        reportLabel={reportLabel}
        // The strip's counts survive a day change on purpose. `buildDays`
        // counts over the WHOLE unranged read, not the requested day, so the
        // list it returns is a set of facts about specific dates — "Sep 8 had
        // four entries" does not stop being true because you navigated to
        // Sep 9. Holding it is what lets the strip keep its dots instead of
        // emptying and refilling on every arrow press.
        days={held.current?.days ?? []}
        entries={
          held.current
            ? (held.current.days.find((entry) => entry.day === dayKey(day.from))?.entries ?? 0)
            : null
        }
        scopeLabel={held.current?.scopeLabel}
        onPickDay={(key) => onNavigate(hrefWithParam("day", key))}
      />
    );
  }

  const params = new URLSearchParams(search);
  const activeSiteId = params.get("site") ?? props.scopeSiteId ?? null;
  const activeCategory = params.get("cat");
  const needsOk = params.get("needsok") === "1";

  const tiles = buildTiles(data);
  const siteNameOf = (jobSiteId: string) =>
    data.hero.sites.find((site) => site.id === jobSiteId)?.name || jobSiteId;

  /**
   * Every row the day produced, in ONE list.
   *
   * `cards` and `outsideTaxonomy` are two server-side partitions of the same
   * rows, and 8a's timeline groups by walk rather than by category, so the
   * split stops being a layout and becomes a detail about which chips exist.
   * Folding them together here is what keeps the file's oldest promise: a
   * daily-log row is never silently dropped, whatever category it carries.
   */
  const allRows: ConsoleRow[] = [
    ...data.cards.flatMap((card) => card.rows),
    ...data.outsideTaxonomy.flatMap((group) => group.rows),
  ];
  const pendingTotal = allRows.filter((row) => row.reviewStatus === "pending").length;

  const passes = (row: ConsoleRow) =>
    (activeCategory === null || row.category === activeCategory) &&
    (!needsOk || row.reviewStatus === "pending");

  const dayWalks: DailyLogWalkRow[] = (data.walks ?? []).map((walk) => {
    if (walk.autodeskTemplate) {
      reportTemplates.current.set(walk.visitId, walk.autodeskTemplate);
      return walk;
    }
    const kept = walk.autodeskPublishing ? reportTemplates.current.get(walk.visitId) : undefined;
    return kept ? { ...walk, autodeskTemplate: { ...kept, syncStatus: "pending" } } : walk;
  });
  const templateWalks = new Set(dayWalks.filter(walk => !!walk.autodeskTemplate).map(walk => walk.visitId));
  /** Manpower is drawn ONCE, in its own table. Leaving it in the walk cards
   *  as well would list the same companies twice on the same page. */
  const manpowerRows = allRows.filter((row) => row.category === "manpower" && !templateWalks.has(row.visitId) && passes(row));
  const showPhotos = (activeCategory === null || activeCategory === "photos") && !needsOk;
  /**
   * The walk cards (design "Daily log"): each walk's header, ONE Photos row
   * for its photos, then its other entries. Walk order is the server's
   * (newest first); entries within a walk run forward in time. Rows whose
   * walk the server did not describe land in a final group, never dropped.
   */
  const walkGroups = buildWalkGroups({
    walks: dayWalks,
    rows: allRows.filter((row) => !(row.category === "manpower" && !templateWalks.has(row.visitId))),
    photosByWalk: walkPhotos,
    showPhotos,
    passes,
  });
  const flatRows = flattenGroups(walkGroups);
  /** Photos counts PHOTOS, whatever the filter, so the pill reads the same. */
  const allPhotoCount = buildWalkGroups({ walks: dayWalks, rows: allRows, photosByWalk: walkPhotos, showPhotos: true, passes: () => false })
    .reduce((sum, group) => sum + (group.photos?.photos.length ?? 0), 0);
  const pills = data.cards
    .filter((card) => card.category !== "photos")
    .map((card) => ({ category: card.category as string, label: card.label, count: card.rows.length }));
  if (allPhotoCount > 0 || data.cards.some((card) => card.category === "photos")) {
    pills.push({ category: "photos", label: "Photos", count: allPhotoCount });
  }
  /** `All` counts the day's entries — the same number its week-strip cell
   *  shows — while `Photos` counts photos. */
  const entryTotal = allRows.length;

  const selected = selectedId
    ? (allRows.find((row) => row.id === selectedId) ?? null)
    : null;
  const selectedWalk = selected
    ? (dayWalks.find((walk) => walk.visitId === selected.visitId) ?? null)
    : null;

  /** Which project this report covers, named whenever there is one answer. */
  const onlySite = data.hero.sites.length === 1 ? data.hero.sites[0] : undefined;
  const scopeLabel = activeSiteId
    ? siteNameOf(activeSiteId)
    : onlySite
      ? onlySite.name || onlySite.id
      : plural(data.hero.sites.length, "project", "projects");

  held.current = { days: data.days ?? [], scopeLabel };

  const nothingShown = manpowerRows.length === 0 && walkGroups.length === 0;
  const emptyDay = allRows.length === 0;

  /**
   * What to CALL the project in the empty state (10a: "No walks on 66 Pearl
   * yet today").
   *
   * Never the raw id — 10a's own note says so, and `scopeLabel` above falls
   * back to one because the crumb has to name *something*. A site with no
   * name is "this project": less useful than a name and infinitely more
   * useful than 24 hex characters.
   */
  const emptySiteWord = (() => {
    if (activeSiteId) return siteNameOf(activeSiteId) || "this project";
    if (onlySite) return onlySite.name || "this project";
    return null;
  })();

  /**
   * The nearest day that DID log something — the way out of a quiet day.
   *
   * Off `data.days`, which is every day in scope with entries, so this
   * reaches forward as well as back: opening a past empty day while later
   * days have entries used to dead-end, because `lastLoggedBefore` only ever
   * looks backwards. Prefer the most recent day before this one (the usual
   * case, and the one 10a draws); fall back to the earliest after it.
   */
  const nearestLoggedDay = (() => {
    const key = dayKey(day.from);
    const rows = data.days ?? [];
    // `days` is newest-first, so the first row before the day IS the nearest.
    const before = rows.find((entry) => entry.day < key);
    if (before) return before;
    const after = rows.filter((entry) => entry.day > key);
    return after.length > 0 ? after[after.length - 1]! : null;
  })();

  // Autodesk: one project's day IS one Form. The page is that report, as an
  // editor ("Daily Report Editor"); the timeline and its category chips are
  // the Procore daily log's shape and do not apply.
  // Every project here is on Autodesk: "All projects" is the list of each
  // project's report for the day ("Daily Report Editor", no project chosen).
  const allAutodesk = !activeSiteId && data.hero.sites.length > 0 &&
    data.hero.sites.every((site) => site.syncSource === "autodesk");
  if ((props.integration === "autodesk" && activeSiteId) || allAutodesk) {
    const reportWalks = dayWalks
      .filter((walk) => walk.autodeskTemplate?.formId)
      .sort((a, b) => Date.parse(a.startedAt ?? "") - Date.parse(b.startedAt ?? ""));
    const primary = [...reportWalks].sort((a, b) =>
      Date.parse(b.autodeskTemplate?.attemptedAt ?? b.autodeskTemplate?.syncedAt ?? "") -
      Date.parse(a.autodeskTemplate?.attemptedAt ?? a.autodeskTemplate?.syncedAt ?? ""))[0];
    const sameForm = reportWalks.filter((walk) => walk.autodeskTemplate?.formId === primary?.autodeskTemplate?.formId);
    const reportVisitIds = new Set(sameForm.map((walk) => walk.visitId));
    const reportRows = allRows.filter((row) => reportVisitIds.has(row.visitId));
    const reviewHref = props.hrefForReview(activeSiteId ?? undefined);
    return (
      <ViewBody wide="record">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, paddingTop: 10 }}>
          <SiteChip
            allLabel="All projects"
            tiles={tiles}
            activeSiteId={activeSiteId}
            open={siteMenuOpen}
            onOpenChange={setSiteMenuOpen}
            onPick={(siteId) => {
              setSiteMenuOpen(false);
              onNavigate(hrefWithParam("site", siteId));
            }}
          />
          <span style={{ ...META, fontSize: 9.5, letterSpacing: "1.4px", color: ADR.label }}>
            {activeSiteId ? "Daily reports" : "Daily reports · All projects"}
          </span>
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <WeekStrip
            day={day}
            days={data.days ?? []}
            countWalks
            onPick={(key) => onNavigate(hrefWithParam("day", key))}
            style={{ flex: 1, minWidth: 0 }}
          />
          <JumpToDate
            day={dayKey(day.from)}
            today={day.today}
            days={data.days ?? []}
            onPick={(key) => onNavigate(hrefWithParam("day", key))}
          />
        </div>
        {refreshFailure && <div style={{ marginTop: 14 }}><StaleBanner failure={refreshFailure} since={data.generatedAt} /></div>}
        {!activeSiteId ? (
          <AutodeskDayReports
            dayLabel={day.from.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            dayShort={day.from.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            sites={data.hero.sites}
            walks={dayWalks}
            rows={allRows}
            hrefForSite={(siteId) => hrefWithParam("site", siteId)}
            onNavigate={onNavigate}
          />
        ) : primary?.autodeskTemplate ? (
          <AutodeskDailyReport
            key={`${primary.autodeskTemplate.formId}`}
            // One Form, several walks: each walk's read carries the cells and
            // sync marks of its own records, so the report merges them.
            template={{
              ...primary.autodeskTemplate,
              cells: Object.assign({}, ...sameForm.map((walk) => walk.autodeskTemplate?.cells ?? {})),
              syncedItemIds: sameForm.flatMap((walk) => walk.autodeskTemplate?.syncedItemIds ?? []),
              attachmentMediaIds: [...new Set(sameForm.flatMap((walk) => walk.autodeskTemplate?.attachmentMediaIds ?? []))],
              syncStatus: sameForm.some((walk) => walk.autodeskTemplate?.syncStatus === "pending") ? "pending" : primary.autodeskTemplate.syncStatus,
            }}
            walks={sameForm}
            rows={reportRows}
            jobSiteId={activeSiteId}
            search={search}
            refreshKey={refreshKey + editRevision}
            onChanged={() => setEditRevision((value) => value + 1)}
            onSelectEntry={(id) => void selectEntry(id)}
            hrefForWalk={props.hrefForWalk}
            onNavigate={onNavigate}
            review={pendingTotal > 0 ? { count: pendingTotal, href: reviewHref } : undefined}
          />
        ) : emptyDay ? (
          <div style={{ marginTop: 22 }}>
            <QuietDayPanel
              day={day}
              reportLabel={reportLabel}
              siteWord={emptySiteWord}
              walksThatDay={data.walksThatDay}
              nearest={nearestLoggedDay}
              onNavigate={onNavigate}
              hrefWithParam={hrefWithParam}
            />
          </div>
        ) : (
          <div style={{ marginTop: 22 }}>
            <EmptyPanel
              title={pendingTotal > 0 ? "Not in Autodesk yet" : "No daily report"}
              body={pendingTotal > 0
                ? `${plural(pendingTotal, "record", "records")} waiting in review.`
                : "Nothing from this day was accepted."}
              action={pendingTotal > 0 ? { label: "Review", href: reviewHref, onClick: () => onNavigate(reviewHref) } : undefined}
            />
          </div>
        )}
        {selected && (
          <div
            data-testid="autodesk-report-entry-drawer"
            style={{ position: "fixed", top: 16, right: 16, bottom: 16, width: 380, zIndex: 40, overflowY: "auto", borderRadius: 16, boxShadow: "0 24px 60px rgba(20,17,12,.22)" }}
          >
            <DailyLogEntryEditor
              key={`${query}:${selected.id}`}
              search={search}
              refreshKey={refreshKey}
              beforeLeave={beforeLeave}
              onSaved={() => setEditRevision((value) => value + 1)}
              row={selected}
              walk={selectedWalk}
              reviewHref={reviewRecordHref(props.hrefForReview(selected.jobSiteId), selected)}
              detailHref={hrefForItem(selected.id)}
              onOpenDetail={() => onOpenItem(selected.id)}
              onNavigate={onNavigate}
              onClose={() => setSelectedId(null)}
            />
          </div>
        )}
      </ViewBody>
    );
  }

  const byWalk = !flat;
  const selectedPhotos = selectedPhotosWalk
    ? walkGroups.find((group) => group.walk?.visitId === selectedPhotosWalk)?.photos ?? null
    : null;
  const paneOpen = !!selected || !!selectedPhotos;
  const handlers: DailyLogRowHandlers = {
    selectedId: selected?.id ?? null,
    selectedPhotosWalk: selectedPhotos ? selectedPhotosWalk : null,
    hrefForItem,
    hrefForWalk: props.hrefForWalk,
    onSelect: (id) => void selectEntry(id),
    onOpenPhotos: (visitId) => void openPhotos(visitId),
  };
  const creatorOf = (row: ConsoleRow) => dayWalks.find((walk) => walk.visitId === row.visitId)?.walkerName;

  const header = (
    <ViewHeader
      wide="record"
      // The design's eyebrow: what this is, and which project it is of.
      crumb={`${reportLabel} · ${scopeLabel}`}
      // The date, always. "Today" belongs to the week strip below.
      title={dayLabel(day.from)}
    >
      {/* The strip, and the way off it (Jump to date) — the strip moves a
          week at a time, so a walk from six weeks ago is one pick away. */}
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <WeekStrip
          day={day}
          days={data.days ?? []}
          onPick={(key) => onNavigate(hrefWithParam("day", key))}
          style={{ flex: 1, minWidth: 0 }}
        />
        <JumpToDate
          day={dayKey(day.from)}
          today={day.today}
          days={data.days ?? []}
          onPick={(key) => onNavigate(hrefWithParam("day", key))}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <FilterChips
          scope={!activeSiteId && data.hero.sites.length > 1 ? (
            <SiteChip
              tiles={tiles}
              activeSiteId={activeSiteId}
              open={siteMenuOpen}
              onOpenChange={setSiteMenuOpen}
              onPick={(siteId) => {
                setSiteMenuOpen(false);
                onNavigate(hrefWithParam("site", siteId));
              }}
            />
          ) : undefined}
          pills={pills}
          activeCategory={activeCategory}
          needsOk={needsOk}
          total={entryTotal}
          pendingTotal={pendingTotal}
          byWalk={byWalk}
          onPickCategory={(category) => onNavigate(hrefWithParam("cat", category))}
          onToggleNeedsOk={() => onNavigate(hrefWithParam("needsok", needsOk ? null : "1"))}
          onToggleByWalk={() => onNavigate(hrefWithParam("flat", byWalk ? "1" : null))}
        />
      </div>
    </ViewHeader>
  );

  return (
    <ViewBody wide="record" header={header}>
      <DailyLogStyles />
      {refreshFailure && <div style={{ marginTop: 14 }}><StaleBanner failure={refreshFailure} since={data.generatedAt} /></div>}

      {emptyDay ? (
        <div style={{ marginTop: 14 }}>
          {data.hero.sites.length === 0 ? (
            /* No projects at all — there is no week to show where the
               entries are, because there has never been an entry. */
            <EmptyPanel
              title="No projects yet"
              body={`This workspace has no job sites. ${reportLabel}s appear here once a site is created and walked.`}
            />
          ) : (
            <QuietDayPanel
              day={day}
              reportLabel={reportLabel}
              siteWord={emptySiteWord}
              walksThatDay={data.walksThatDay}
              nearest={nearestLoggedDay}
              onNavigate={onNavigate}
              hrefWithParam={hrefWithParam}
            />
          )}
        </div>
      ) : (
        /*
          Two columns: the tables, and — when a row is open — the 400px pane.
          Both start on the same line, so the pane's top edge sits level with
          the first table (Manpower, or the first walk when Manpower is
          filtered out); the pane is sticky inside the page's scroller.
          Below the console's breakpoint `.dlcols` stacks them.
        */
        <div
          className={paneOpen ? "dlcols" : undefined}
          data-testid="daily-log-columns"
          data-pane={paneOpen ? "open" : "closed"}
          style={{
            display: "grid",
            gridTemplateColumns: paneOpen ? "minmax(0, 1fr) 400px" : "minmax(0, 1fr)",
            gap: 20,
            alignItems: "start",
            marginTop: 14,
          }}
        >
          {/* Keyed on the filter so `.rise` replays when it changes. */}
          <div
            key={`${activeCategory ?? "all"}|${needsOk ? "ok" : "any"}|${byWalk ? "walk" : "flat"}`}
            className="rise"
            data-testid="daily-log-tables"
            style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}
          >
            <ManpowerTable
              rows={manpowerRows}
              flat={!byWalk}
              selectedId={selected?.id ?? null}
              creatorOf={creatorOf}
              hrefForItem={hrefForItem}
              onSelect={(id) => void selectEntry(id)}
            />

            {byWalk
              ? walkGroups.map((group) => group.walk?.autodeskTemplate ? (
                  <div key={group.walk.visitId} data-testid="walk-timeline" data-visit-id={group.walk.visitId} style={{ border: `1px solid ${DL.hairline}`, borderRadius: 14, background: DL.card, overflow: "hidden" }}>
                    <WalkHeader walk={group.walk} />
                    <AutodeskReportStrip
                      template={group.walk.autodeskTemplate}
                      rows={allRows.filter((row) => row.visitId === group.walk!.visitId)}
                      href={hrefWithParam("site", allRows.find((row) => row.visitId === group.walk!.visitId)?.jobSiteId ?? null)}
                      onNavigate={onNavigate}
                    />
                  </div>
                ) : (
                  <WalkGroupCard key={group.walk?.visitId ?? "no-walk"} group={group} handlers={handlers} />
                ))
              : <FlatTable rows={flatRows} handlers={handlers} />}

            {/* A filter that hides everything says so, rather than leaving a
                blank column that reads as a broken page. */}
            {nothingShown && (
              <EmptyPanel
                title="Nothing matches this filter"
                body="This day logged entries, but none under the category or state you picked."
                action={{
                  label: "Show everything logged this day",
                  href: hrefWithParam("cat", null),
                  onClick: () => onNavigate(hrefWithParam("cat", null)),
                }}
              />
            )}

            {data.quietCategories.length > 0 && data.hero.sites.some(site => site.syncSource !== "autodesk") && (
              <QuietLine>{quietSentence(data.quietCategories.map((entry) => entry.label), day)}</QuietLine>
            )}
          </div>

          {selected ? (
            <DailyLogEntryEditor
              key={`${query}:${selected.id}`}
              search={search}
              refreshKey={refreshKey}
              beforeLeave={beforeLeave}
              onSaved={() => setEditRevision((value) => value + 1)}
              onDeleted={() => { setSelectedId(null); setEditRevision((value) => value + 1); }}
              row={selected}
              walk={selectedWalk}
              viewerName={data.caller?.name}
              photoFallback={(mediaId) => {
                const photo = walkPhotos.get(selected.visitId)?.find((entry) => entry.mediaId === mediaId);
                return photo?.url ?? photo?.thumbnailUrl ?? null;
              }}
              reviewHref={reviewRecordHref(props.hrefForReview(selected.jobSiteId), selected)}
              detailHref={hrefForItem(selected.id)}
              onOpenDetail={() => onOpenItem(selected.id)}
              onNavigate={onNavigate}
              onClose={() => setSelectedId(null)}
            />
          ) : selectedPhotos ? (
            <DailyLogPhotosPane
              key={selectedPhotos.walk.visitId}
              walk={selectedPhotos.walk}
              photos={selectedPhotos.photos}
              jobSiteId={activeSiteId ?? allRows.find((row) => row.visitId === selectedPhotos.walk.visitId)?.jobSiteId ?? ""}
              search={search}
              focusId={focusPhoto}
              onFocus={setFocusPhoto}
              onClose={() => { setSelectedPhotosWalk(null); setFocusPhoto(null); }}
              onChanged={() => setPhotosRevision((value) => value + 1)}
            />
          ) : null}
        </div>
      )}
    </ViewBody>
  );

}
